import React, { useCallback, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight,
  Briefcase,
  Building2,
  Check,
  ClipboardCheck,
  CreditCard,
  Globe,
  HelpCircle,
  Layers,
  MapPin,
  Shield,
  Sparkles,
  UserCog,
  Wrench,
  Zap,
} from 'lucide-react';
import { digitalCardStyles } from '../digitalcard/digitalCardStyles';
import { fieldTeamStyles } from './fieldTeamStyles';
import FieldTeamNav from './FieldTeamNav';
import FieldTeamFooter from './FieldTeamFooter';
import FieldTeamLoginCard from './FieldTeamLoginCard';
import FieldTeamStickyCta from './FieldTeamStickyCta';
import { HeroDevicePair, LaptopMockup, ProductShowcaseStage } from './DeviceMockups';
import PreviewPhoneFrame from '../digitalcard/PreviewPhoneFrame';
import { FIELD_TEAM_ASSETS } from './fieldTeamAssets';
import { getFieldTeamFunnelSolutions } from './fieldTeamSolutions';
import type { FieldTeamAuthState } from './fieldTeamAuth';
import {
  CONTACT_PATH,
  DEMO_PATH,
  DOWNLOAD_PATH,
  FEATURES_SECTION_ID,
  HOW_SECTION_ID,
  INDUSTRIES_SECTION_ID,
  LOGIN_SECTION_ID,
  PRICING_SECTION_ID,
  PRODUCT_SECTION_ID,
  SOLUTIONS_SECTION_ID,
  fieldTeamHomePath,
  fieldTeamSolutionPath,
  isFieldTeamHomePath,
  scrollToFieldTeamSection,
} from './fieldTeamRoutes';
import { CRM_PLAN_FALLBACK } from '../../config/crmPlans';
import { useLocalizedCrmPlans } from '../../utils/localizedCrmPlans';

type Props = FieldTeamAuthState;

const METHODS = [
  'ftl_problem_method_whatsapp',
  'ftl_problem_method_calls',
  'ftl_problem_method_sheets',
  'ftl_problem_method_paper',
  'ftl_problem_method_registers',
  'ftl_problem_method_verbal',
  'ftl_problem_method_dbs',
] as const;

const QUESTIONS = [
  { t: 'ftl_q_who_t', d: 'ftl_q_who_d' },
  { t: 'ftl_q_where_t', d: 'ftl_q_where_d' },
  { t: 'ftl_q_when_t', d: 'ftl_q_when_d' },
  { t: 'ftl_q_what_t', d: 'ftl_q_what_d' },
  { t: 'ftl_q_done_t', d: 'ftl_q_done_d' },
  { t: 'ftl_q_evidence_t', d: 'ftl_q_evidence_d' },
  { t: 'ftl_q_issue_t', d: 'ftl_q_issue_d' },
  { t: 'ftl_q_next_t', d: 'ftl_q_next_d' },
] as const;

const MOBILE_POINTS = [
  'ftl_platform_mobile_1',
  'ftl_platform_mobile_2',
  'ftl_platform_mobile_3',
  'ftl_platform_mobile_4',
] as const;

const DASH_POINTS = [
  'ftl_platform_dash_1',
  'ftl_platform_dash_2',
  'ftl_platform_dash_3',
  'ftl_platform_dash_4',
] as const;

const CAPABILITIES = [
  { icon: ClipboardCheck, t: 'ftl_cap_attendance_t', d: 'ftl_cap_attendance_d' },
  { icon: MapPin, t: 'ftl_cap_gps_t', d: 'ftl_cap_gps_d' },
  { icon: Sparkles, t: 'ftl_cap_route_t', d: 'ftl_cap_route_d' },
  { icon: Layers, t: 'ftl_cap_task_t', d: 'ftl_cap_task_d' },
  { icon: Building2, t: 'ftl_cap_site_t', d: 'ftl_cap_site_d' },
  { icon: Briefcase, t: 'ftl_cap_crm_t', d: 'ftl_cap_crm_d' },
  { icon: MapPin, t: 'ftl_cap_visit_t', d: 'ftl_cap_visit_d' },
  { icon: Shield, t: 'ftl_cap_incident_t', d: 'ftl_cap_incident_d' },
  { icon: UserCog, t: 'ftl_cap_inspect_t', d: 'ftl_cap_inspect_d' },
  { icon: Zap, t: 'ftl_cap_perf_t', d: 'ftl_cap_perf_d' },
] as const;

const INDUSTRIES = [
  ['ftl_ind_sales_t', 'ftl_ind_sales_d'],
  ['ftl_ind_security_t', 'ftl_ind_security_d'],
  ['ftl_ind_cleaning_t', 'ftl_ind_cleaning_d'],
  ['ftl_ind_facilities_t', 'ftl_ind_facilities_d'],
  ['ftl_ind_tech_t', 'ftl_ind_tech_d'],
  ['ftl_ind_telecom_t', 'ftl_ind_telecom_d'],
  ['ftl_ind_fmcg_t', 'ftl_ind_fmcg_d'],
  ['ftl_ind_pharma_t', 'ftl_ind_pharma_d'],
  ['ftl_ind_finance_t', 'ftl_ind_finance_d'],
  ['ftl_ind_insurance_t', 'ftl_ind_insurance_d'],
  ['ftl_ind_realestate_t', 'ftl_ind_realestate_d'],
  ['ftl_ind_property_t', 'ftl_ind_property_d'],
  ['ftl_ind_mfg_t', 'ftl_ind_mfg_d'],
  ['ftl_ind_construction_t', 'ftl_ind_construction_d'],
  ['ftl_ind_pro_t', 'ftl_ind_pro_d'],
  ['ftl_ind_agency_t', 'ftl_ind_agency_d'],
] as const;

const DIFFS = [
  { bar: 'blue', iconTone: 'blue', t: 'ftl_diff_people_t', d: 'ftl_diff_people_d' },
  { bar: 'violet', iconTone: 'violet', t: 'ftl_diff_places_t', d: 'ftl_diff_places_d' },
  { bar: 'emerald', iconTone: 'emerald', t: 'ftl_diff_work_t', d: 'ftl_diff_work_d' },
  { bar: 'blue', iconTone: 'blue', t: 'ftl_diff_results_t', d: 'ftl_diff_results_d' },
] as const;

const WITHOUT = [
  'ftl_compare_wo_1', 'ftl_compare_wo_2', 'ftl_compare_wo_3', 'ftl_compare_wo_4',
  'ftl_compare_wo_5', 'ftl_compare_wo_6', 'ftl_compare_wo_7', 'ftl_compare_wo_8', 'ftl_compare_wo_9',
] as const;

const WITH = [
  'ftl_compare_w_1', 'ftl_compare_w_2', 'ftl_compare_w_3', 'ftl_compare_w_4',
  'ftl_compare_w_5', 'ftl_compare_w_6', 'ftl_compare_w_7', 'ftl_compare_w_8', 'ftl_compare_w_9',
] as const;

const STEPS = [
  { n: 1, titleKey: 'ftl_step1_title', descKey: 'ftl_step1_desc' },
  { n: 2, titleKey: 'ftl_step2_title', descKey: 'ftl_step2_desc' },
  { n: 3, titleKey: 'ftl_step3_title', descKey: 'ftl_step3_desc' },
  { n: 4, titleKey: 'ftl_step4_title', descKey: 'ftl_step4_desc' },
  { n: 5, titleKey: 'ftl_step5_title', descKey: 'ftl_step5_desc' },
  { n: 6, titleKey: 'ftl_step6_title', descKey: 'ftl_step6_desc' },
] as const;

const FAQS = [
  { q: 'ftl_faq1_q', a: 'ftl_faq1_a' },
  { q: 'ftl_faq2_q', a: 'ftl_faq2_a' },
  { q: 'ftl_faq3_q', a: 'ftl_faq3_a' },
  { q: 'ftl_faq4_q', a: 'ftl_faq4_a' },
] as const;

const KNOW = ['ftl_final_know1', 'ftl_final_know2', 'ftl_final_know3', 'ftl_final_know4', 'ftl_final_know5'] as const;

const STORY_TEAMS = [
  { key: 'ftl_story_sales', icon: Briefcase },
  { key: 'ftl_story_tech', icon: Wrench },
  { key: 'ftl_story_supervisor', icon: UserCog },
  { key: 'ftl_story_security', icon: Shield },
] as const;

export const FieldTeamLanding: React.FC<Props> = (auth) => {
  const { t } = useTranslation();
  const location = useLocation();
  const plans = useLocalizedCrmPlans(CRM_PLAN_FALLBACK);
  const home = fieldTeamHomePath(location.pathname);
  const funnelSolutions = getFieldTeamFunnelSolutions();
  const openSignup = useCallback(() => auth.setSignupOpen(true), [auth]);

  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (!hash) {
      if (isFieldTeamHomePath(location.pathname)) window.scrollTo(0, 0);
      return;
    }
    const tryScroll = () => {
      const el = document.getElementById(hash);
      if (!el) return false;
      scrollToFieldTeamSection(hash);
      return true;
    };
    if (tryScroll()) return;
    const retry = window.setInterval(() => {
      if (tryScroll()) window.clearInterval(retry);
    }, 100);
    const stop = window.setTimeout(() => window.clearInterval(retry), 3000);
    return () => {
      window.clearInterval(retry);
      window.clearTimeout(stop);
    };
  }, [location.pathname, location.hash]);

  return (
    <div className="dcl-root dcl-root--linktree ftl-root">
      <style>{digitalCardStyles}</style>
      <style>{fieldTeamStyles}</style>
      <FieldTeamNav activePath={home} onGetStarted={openSignup} />

      <section className="dcl-hero-lt" id="ftl-hero">
        <div className="dcl-container dcl-hero-lt-inner">
          <div className="dcl-hero-lt-copy">
            <div className="dcl-section-badge">
              <Zap size={14} />
              {t('ftl_nav_tagline')}
            </div>
            <h1 className="dcl-hero-lt-title">{t('ftl_hero_title')}</h1>
            <p className="dcl-hero-lt-sub">{t('ftl_hero_sub')}</p>
            <p className="ftl-market-line">{t('ftl_hero_market')}</p>
            <p className="ftl-africa-line">{t('ftl_hero_africa')}</p>
            <div className="dcl-hero-lt-actions" style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              <button type="button" className="dcl-btn dcl-btn--primary dcl-btn--pill" onClick={openSignup}>
                {t('ftl_cta_signup_free')}
                <ArrowRight size={18} />
              </button>
              <Link to={DEMO_PATH} className="dcl-btn dcl-btn--secondary dcl-btn--pill">
                {t('ftl_cta_demo')}
              </Link>
            </div>
            <p className="dcl-signin-link dcl-signin-link--hero ftl-hero-text-links">
              <a href={`#${LOGIN_SECTION_ID}`}>{t('ftl_cta_signin')}</a>
              <span aria-hidden="true"> · </span>
              <Link to={CONTACT_PATH}>{t('ftl_nav_contact')}</Link>
              <span aria-hidden="true"> · </span>
              <a href={DOWNLOAD_PATH}>{t('ftl_nav_download')}</a>
            </p>
          </div>
          <div className="dcl-hero-lt-visual">
            <HeroDevicePair
              laptopSrc={FIELD_TEAM_ASSETS.crmDashboard}
              laptopAlt={t('ftl_asset_crm_dashboard_alt')}
              phoneSrc={FIELD_TEAM_ASSETS.mobileDashboard}
              phoneAlt={t('ftl_asset_mobile_dashboard_alt')}
            />
          </div>
        </div>
      </section>

      <section className="dcl-section dcl-section--white dcl-section--mesh">
        <div className="dcl-container">
          <div className="dcl-section-header">
            <div className="dcl-section-badge">
              <Layers size={14} />
              {t('ftl_story_badge')}
            </div>
            <h2 className="dcl-section-title dcl-section-title--center">{t('ftl_story_title')}</h2>
            <p className="dcl-section-sub dcl-section-sub--center" style={{ marginTop: 12 }}>{t('ftl_story_sub')}</p>
          </div>
          <div className="ftl-story">
            <div className="ftl-story-row">
              {STORY_TEAMS.map(({ key, icon: Icon }) => (
                <div key={key} className="ftl-story-chip">
                  <Icon size={18} />
                  {t(key)}
                </div>
              ))}
            </div>
            <div className="ftl-story-arrow" aria-hidden="true">↓</div>
            <div className="ftl-story-node ftl-story-node--app">{t('ftl_story_app')}</div>
            <div className="ftl-story-arrow" aria-hidden="true">↓</div>
            <div className="ftl-story-node ftl-story-node--platform">{t('ftl_story_platform')}</div>
            <div className="ftl-story-arrow" aria-hidden="true">↓</div>
            <div className="ftl-story-node ftl-story-node--mgmt">{t('ftl_story_mgmt')}</div>
          </div>
        </div>
      </section>

      <section className="dcl-section dcl-section--soft dcl-section--mesh">
        <div className="dcl-container">
          <div className="dcl-section-header">
            <div className="dcl-section-badge">
              <HelpCircle size={14} />
              {t('ftl_problem_badge')}
            </div>
            <h2 className="dcl-section-title dcl-section-title--center">{t('ftl_problem_title')}</h2>
            <p className="dcl-section-sub dcl-section-sub--center" style={{ marginTop: 12 }}>{t('ftl_problem_sub')}</p>
          </div>
          <div className="dcl-chip-row">
            {METHODS.map((key) => (
              <span key={key} className="dcl-chip">{t(key)}</span>
            ))}
          </div>
          <div className="ftl-q-grid">
            {QUESTIONS.map((item) => (
              <article key={item.t} className="ftl-q-card">
                <h3>{t(item.t)}</h3>
                <p>{t(item.d)}</p>
              </article>
            ))}
          </div>
          <p className="ftl-section-close">{t('ftl_problem_close')}</p>
        </div>
      </section>

      <section className={`dcl-section dcl-section--white dcl-section--mesh ftl-scroll-target`} id={PRODUCT_SECTION_ID}>
        <div className="dcl-container">
          <div className="dcl-section-header">
            <div className="dcl-section-badge">
              <Sparkles size={14} />
              {t('ftl_platform_badge')}
            </div>
            <h2 className="dcl-section-title dcl-section-title--center">{t('ftl_platform_title')}</h2>
            <p className="dcl-section-sub dcl-section-sub--center" style={{ marginTop: 12 }}>{t('ftl_platform_sub')}</p>
          </div>
          <div className="ftl-platform-grid">
            <article className="ftl-platform-card">
              <div className="ftl-platform-visual">
                <PreviewPhoneFrame
                  src={FIELD_TEAM_ASSETS.mobileDashboard}
                  alt={t('ftl_asset_mobile_dashboard_alt')}
                  size="sm"
                />
              </div>
              <h3 className="dcl-pillar-title">{t('ftl_platform_mobile_title')}</h3>
              <ul className="ftl-feature-list">
                {MOBILE_POINTS.map((key) => (
                  <li key={key}><span className="dcl-pricing-feature-icon"><Check size={12} /></span>{t(key)}</li>
                ))}
              </ul>
            </article>
            <article className="ftl-platform-card">
              <div className="ftl-platform-visual">
                <LaptopMockup src={FIELD_TEAM_ASSETS.crmDashboard} alt={t('ftl_asset_crm_dashboard_alt')} />
              </div>
              <h3 className="dcl-pillar-title">{t('ftl_platform_dash_title')}</h3>
              <ul className="ftl-feature-list">
                {DASH_POINTS.map((key) => (
                  <li key={key}><span className="dcl-pricing-feature-icon"><Check size={12} /></span>{t(key)}</li>
                ))}
              </ul>
            </article>
          </div>
          <p className="ftl-section-close">{t('ftl_platform_close')}</p>
        </div>
      </section>

      <section className={`dcl-section dcl-section--white dcl-section--mesh ftl-scroll-target`} id={SOLUTIONS_SECTION_ID}>
        <div className="dcl-container">
          <div className="dcl-section-header">
            <div className="dcl-section-badge">
              <Layers size={14} />
              {t('ftl_solutions_badge')}
            </div>
            <h2 className="dcl-section-title dcl-section-title--center">{t('ftl_solutions_title')}</h2>
            <p className="dcl-section-sub dcl-section-sub--center" style={{ marginTop: 12 }}>{t('ftl_solutions_sub')}</p>
          </div>
          <div className="ftl-solution-grid">
            {funnelSolutions.map(({ slug, icon: Icon, bar, iconTone }) => (
              <Link key={slug} to={fieldTeamSolutionPath(slug)} className="ftl-solution-card">
                <div className={`dcl-pillar-card-bar dcl-pillar-card-bar--${bar}`} aria-hidden="true" />
                <div className="ftl-solution-card-body">
                  <div className={`dcl-pillar-icon dcl-pillar-icon--${iconTone}`}>
                    <Icon size={22} strokeWidth={2.2} />
                  </div>
                  <h3 className="dcl-pillar-title">{t(`ftl_sol_${slug}_title`)}</h3>
                  <p className="dcl-pillar-desc">{t(`ftl_sol_${slug}_card`)}</p>
                  <span className="ftl-solution-cta">
                    {t(`ftl_sol_${slug}_cta`)}
                    <ArrowRight size={16} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className={`dcl-section dcl-section--soft dcl-section--mesh ftl-scroll-target`} id={FEATURES_SECTION_ID}>
        <div className="dcl-container">
          <div className="dcl-section-header">
            <div className="dcl-section-badge">
              <Sparkles size={14} />
              {t('ftl_capabilities_badge')}
            </div>
            <h2 className="dcl-section-title dcl-section-title--center">{t('ftl_capabilities_title')}</h2>
          </div>
          <div className="dcl-feature-grid ftl-cap-grid">
            {CAPABILITIES.map(({ icon: Icon, t: titleKey, d }) => (
              <article key={titleKey} className="dcl-feature-card">
                <div className="dcl-feature-icon"><Icon size={20} /></div>
                <h3 className="dcl-feature-title">{t(titleKey)}</h3>
                <p className="dcl-feature-desc">{t(d)}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className={`dcl-section dcl-section--white dcl-section--mesh ftl-scroll-target`} id={INDUSTRIES_SECTION_ID}>
        <div className="dcl-container">
          <div className="dcl-section-header">
            <div className="dcl-section-badge">
              <Building2 size={14} />
              {t('ftl_industries_badge')}
            </div>
            <h2 className="dcl-section-title dcl-section-title--center">{t('ftl_industries_title')}</h2>
          </div>
          <div className="ftl-industry-grid">
            {INDUSTRIES.map(([titleKey, descKey]) => (
              <article key={titleKey} className="dcl-usecase-card">
                <h3 className="dcl-usecase-title">{t(titleKey)}</h3>
                <p className="dcl-usecase-desc">{t(descKey)}</p>
              </article>
            ))}
          </div>
          <div className="ftl-industry-miss">
            <h3>{t('ftl_industries_miss_title')}</h3>
            <p>{t('ftl_industries_miss_sub')}</p>
            <Link to={CONTACT_PATH} className="dcl-btn dcl-btn--secondary dcl-btn--pill dcl-btn--sm">
              {t('ftl_nav_contact')}
            </Link>
          </div>
        </div>
      </section>

      <section className="dcl-section dcl-section--white dcl-section--mesh">
        <div className="dcl-container">
          <div className="dcl-section-header">
            <div className="dcl-section-badge">
              <MapPin size={14} />
              {t('ftl_diff_badge')}
            </div>
            <h2 className="dcl-section-title dcl-section-title--center">{t('ftl_diff_title')}</h2>
          </div>
          <div className="dcl-pillar-grid">
            {DIFFS.map(({ bar, iconTone, t: titleKey, d }) => (
              <article key={titleKey} className="dcl-pillar-card">
                <div className={`dcl-pillar-card-bar dcl-pillar-card-bar--${bar}`} aria-hidden="true" />
                <div className="dcl-pillar-card-body">
                  <div className={`dcl-pillar-icon dcl-pillar-icon--${iconTone}`}>
                    <Sparkles size={22} strokeWidth={2.2} />
                  </div>
                  <h3 className="dcl-pillar-title">{t(titleKey)}</h3>
                  <p className="dcl-pillar-desc">{t(d)}</p>
                </div>
              </article>
            ))}
          </div>
          <p className="ftl-section-close">{t('ftl_diff_close')}</p>
        </div>
      </section>

      <section className="dcl-section dcl-section--soft dcl-section--mesh">
        <div className="dcl-container">
          <div className="dcl-section-header">
            <div className="dcl-section-badge">
              <Layers size={14} />
              {t('ftl_compare_badge')}
            </div>
          </div>
          <div className="ftl-compare">
            <article className="ftl-compare-card ftl-compare-card--without">
              <h3>{t('ftl_compare_without')}</h3>
              <ul>
                {WITHOUT.map((key) => <li key={key}>{t(key)}</li>)}
              </ul>
            </article>
            <article className="ftl-compare-card ftl-compare-card--with">
              <h3>{t('ftl_compare_with')}</h3>
              <ul>
                {WITH.map((key) => <li key={key}>{t(key)}</li>)}
              </ul>
            </article>
          </div>
        </div>
      </section>

      <section className={`dcl-section dcl-section--lime dcl-section--mesh ftl-scroll-target`} id={HOW_SECTION_ID}>
        <div className="dcl-container">
          <div className="dcl-section-header">
            <div className="dcl-section-badge">
              <Sparkles size={14} />
              {t('ftl_how_badge')}
            </div>
            <h2 className="dcl-section-title dcl-section-title--center">{t('ftl_how_title')}</h2>
          </div>
          <div className="dcl-steps-lt ftl-steps">
            {STEPS.map(({ n, titleKey, descKey }) => (
              <div key={n} className="dcl-step-lt">
                <span className="dcl-step-lt-num">{String(n).padStart(2, '0')}</span>
                <h3 className="dcl-step-lt-title">{t(titleKey)}</h3>
                <p className="dcl-step-lt-desc">{t(descKey)}</p>
              </div>
            ))}
          </div>
          <div className="ftl-how-cta">
            <button type="button" className="dcl-btn dcl-btn--primary dcl-btn--pill" onClick={openSignup}>
              {t('ftl_cta_signup_free')}
              <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </section>

      <section className="dcl-section dcl-section--white dcl-section--mesh">
        <div className="dcl-container">
          <div className="dcl-section-header">
            <div className="dcl-section-badge">
              <Globe size={14} />
              {t('ftl_geo_badge')}
            </div>
            <h2 className="dcl-section-title dcl-section-title--center">{t('ftl_geo_title')}</h2>
            <p className="dcl-section-sub dcl-section-sub--center" style={{ marginTop: 12 }}>{t('ftl_geo_sub')}</p>
          </div>
          <p className="ftl-geo-cities">{t('ftl_geo_cities')}</p>
          <p className="ftl-africa-line ftl-africa-line--center">{t('ftl_hero_africa')}</p>
        </div>
      </section>

      <section className="dcl-section dcl-section--showcase dcl-section--mesh">
        <div className="dcl-container">
          <div className="dcl-section-header">
            <div className="dcl-section-badge">
              <Sparkles size={14} />
              {t('ftl_product_badge')}
            </div>
            <h2 className="dcl-section-title dcl-section-title--center">{t('ftl_product_title')}</h2>
            <p className="dcl-section-sub dcl-section-sub--center" style={{ marginTop: 12 }}>{t('ftl_product_sub')}</p>
          </div>
          <ProductShowcaseStage
            laptopSrc={FIELD_TEAM_ASSETS.crmAiInsights}
            laptopAlt={t('ftl_asset_crm_ai_alt')}
            phones={[
              { src: FIELD_TEAM_ASSETS.mobileDashboard, alt: t('ftl_asset_mobile_dashboard_alt') },
              { src: FIELD_TEAM_ASSETS.mobileAddLead, alt: t('ftl_asset_mobile_lead_alt') },
              { src: FIELD_TEAM_ASSETS.mobileProfile, alt: t('ftl_asset_mobile_profile_alt') },
            ]}
          />
        </div>
      </section>

      <section className={`dcl-section dcl-section--soft dcl-section--mesh ftl-scroll-target`} id={PRICING_SECTION_ID}>
        <div className="dcl-container">
          <div className="dcl-section-header">
            <div className="dcl-section-badge">
              <CreditCard size={14} />
              {t('ftl_nav_pricing')}
            </div>
            <h2 className="dcl-section-title dcl-section-title--center">{t('ftl_pricing_title')}</h2>
            <p className="dcl-section-sub dcl-section-sub--center" style={{ marginTop: 12 }}>{t('ftl_pricing_sub')}</p>
          </div>
          <div className="dcl-pricing-grid">
            {plans.map((plan) => {
              const popular = plan.planId === 'crm_medium';
              return (
                <article key={plan.planId} className={`dcl-pricing-card${popular ? ' dcl-pricing-card--popular' : ''}`}>
                  <div className="dcl-pricing-card-bar" aria-hidden="true" />
                  {popular ? <span className="dcl-pricing-popular">{t('ftl_pricing_most_popular', { defaultValue: 'Most Popular' })}</span> : null}
                  <div className="dcl-pricing-card-body">
                    <h3 className="dcl-pricing-name">{plan.name}</h3>
                    <div className="dcl-pricing-price-row">
                      <span className="dcl-pricing-price">{plan.price}</span>
                      <span className="dcl-pricing-period">{t('home_pricing_per_month', { defaultValue: '/month' })}</span>
                    </div>
                    <p className="dcl-pricing-desc">{plan.description}</p>
                    <ul className="dcl-pricing-features">
                      {plan.features.map((feature) => (
                        <li key={feature}>
                          <span className="dcl-pricing-feature-icon" aria-hidden="true"><Check size={12} /></span>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <button
                      type="button"
                      onClick={openSignup}
                      className={`dcl-btn dcl-btn--pill dcl-btn--sm dcl-pricing-cta${popular ? ' dcl-btn--primary' : ' dcl-btn--secondary'}`}
                    >
                      {t('ftl_cta_signup_free')}
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="dcl-section dcl-section--white dcl-section--mesh">
        <div className="dcl-container">
          <div className="dcl-section-header">
            <div className="dcl-section-badge">
              <HelpCircle size={14} />
              {t('ftl_faq_badge')}
            </div>
            <h2 className="dcl-section-title dcl-section-title--center">{t('ftl_faq_title')}</h2>
          </div>
          <div className="ftl-faq">
            {FAQS.map(({ q, a }) => (
              <article key={q} className="ftl-faq-item">
                <h3>{t(q)}</h3>
                <p>{t(a)}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className={`dcl-section dcl-section--white dcl-section--mesh ftl-scroll-target`} id={LOGIN_SECTION_ID}>
        <div className="dcl-container ftl-login-grid">
          <div>
            <div className="dcl-section-badge">
              <Zap size={14} />
              {t('ftl_login_badge')}
            </div>
            <h2 className="dcl-section-title">{t('ftl_login_title')}</h2>
            <p className="dcl-section-sub" style={{ marginTop: 12, marginBottom: 20 }}>{t('ftl_login_sub')}</p>
            <ul className="ftl-feature-list">
              <li><span className="dcl-pricing-feature-icon"><Check size={12} /></span>{t('ftl_login_point1')}</li>
              <li><span className="dcl-pricing-feature-icon"><Check size={12} /></span>{t('ftl_login_point2')}</li>
              <li><span className="dcl-pricing-feature-icon"><Check size={12} /></span>{t('ftl_login_point3')}</li>
            </ul>
          </div>
          <FieldTeamLoginCard
            email={auth.email}
            password={auth.password}
            showPassword={auth.showPassword}
            loading={auth.loading}
            storeLinks={auth.storeLinks}
            onEmailChange={auth.onEmailChange}
            onPasswordChange={auth.onPasswordChange}
            onTogglePassword={auth.onTogglePassword}
            onSubmit={auth.onSubmit}
            onOpenSignup={openSignup}
          />
        </div>
      </section>

      <section className="dcl-section dcl-final-lt">
        <div className="dcl-container dcl-container--narrow">
          <h2 className="dcl-final-lt-title">{t('ftl_final_title')}</h2>
          <p className="dcl-final-lt-sub">{t('ftl_final_sub')}</p>
          <ul className="ftl-final-know">
            {KNOW.map((key) => <li key={key}>{t(key)}</li>)}
          </ul>
          <div className="ftl-final-actions">
            <button type="button" className="dcl-btn dcl-btn--primary dcl-btn--pill dcl-btn--lg" onClick={openSignup}>
              {t('ftl_cta_signup_free')}
              <ArrowRight size={18} />
            </button>
            <Link to={DEMO_PATH} className="dcl-btn dcl-btn--secondary dcl-btn--pill dcl-btn--lg">
              {t('ftl_cta_demo')}
            </Link>
          </div>
          <p className="dcl-signin-link dcl-signin-link--final">
            <a href={`#${LOGIN_SECTION_ID}`}>{t('ftl_cta_signin')}</a>
            {' · '}
            <Link to={CONTACT_PATH}>{t('ftl_nav_contact')}</Link>
            {' · '}
            <a href={DOWNLOAD_PATH}>{t('ftl_nav_download')}</a>
          </p>
        </div>
      </section>

      <FieldTeamFooter onGetStarted={openSignup} />
      <FieldTeamStickyCta onGetStarted={openSignup} />
    </div>
  );
};

export default FieldTeamLanding;
